/* @jsx h */
import { defineComponent, h, ref } from "vue";
import type { ToolCallInfo } from "../types";

const TOOL_LABELS: Record<string, string> = {
  business_intelligence_search: "Searching schema",
  business_intelligence_execute: "Running query",
};

function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name;
}

function stringify(value: unknown): string {
  if (value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Shows a list of tool calls (business_intelligence_search / business_intelligence_execute) made by
 * the agent while producing this message, with a spinner while running and
 * an expandable input/output preview once finished.
 */
export const ToolCallList = defineComponent<{ toolCalls: ToolCallInfo[] }>({
  name: "ToolCallList",
  props: ["toolCalls"],
  setup(props) {
    const expanded = ref<Record<number, boolean>>({});
    const toggle = (i: number) => {
      expanded.value = { ...expanded.value, [i]: !expanded.value[i] };
    };

    return () => (
      <div class="flex flex-col gap-1 mb-1">
        {props.toolCalls.map((call, i) => (
          <div
            key={i}
            class="rounded-md border border-base-content/10 bg-base-200/40 text-xs"
          >
            <button
              type="button"
              onClick={() => toggle(i)}
              class="flex w-full items-center gap-2 px-2 py-1.5 text-left"
            >
              {call.status === "running" ? (
                <span class="loading loading-spinner loading-xs text-accent" />
              ) : call.status === "error" ? (
                <span
                  class="material-symbols-outlined text-error"
                  style="font-size:14px"
                >
                  error
                </span>
              ) : (
                <span
                  class="material-symbols-outlined text-success"
                  style="font-size:14px"
                >
                  check_circle
                </span>
              )}
              <span class="font-medium text-base-content/70">
                {toolLabel(call.toolName)}
              </span>
              <span
                class="ml-auto material-symbols-outlined text-base-content/30"
                style="font-size:14px"
              >
                {expanded.value[i] ? "expand_less" : "expand_more"}
              </span>
            </button>
            {expanded.value[i] && (
              <div class="border-t border-base-content/10 px-2 py-1.5 space-y-1.5">
                {call.input !== undefined && (
                  <div>
                    <p class="text-base-content/40 mb-0.5">Input</p>
                    <pre class="whitespace-pre-wrap break-all font-mono text-[11px] text-base-content/70">
                      {stringify(call.input)}
                    </pre>
                  </div>
                )}
                {call.output !== undefined && (
                  <div>
                    <p class="text-base-content/40 mb-0.5">Output</p>
                    <pre class="whitespace-pre-wrap break-all font-mono text-[11px] text-base-content/70">
                      {stringify(call.output)}
                    </pre>
                  </div>
                )}
                {call.error && (
                  <div>
                    <p class="text-error/70 mb-0.5">Error</p>
                    <pre class="whitespace-pre-wrap break-all font-mono text-[11px] text-error/80">
                      {call.error}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  },
});
